import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import Field from "../core/ui/forms/field";
import { nameStyle } from "./company-license";
import SessionContext from "SessionContext";
import { useHistory } from "react-router-dom";
import { Button } from "reactstrap";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ConfirmDelete, { FailedToDelete } from "core/ui/confirm-delete";
import { ProgressLoaderSimple } from "../partials/progress-loader";
import ConfirmSuccess from "core/ui/confirm-success";
import UnsavedChanges from "partials/unsaved-changes";

dayjs.extend(utc);
dayjs.extend(timezone);

const initValues = {
  fee: "",
  start_date: dayjs().format('YYYY-MM-DD'),
  end_date: "",
  monthly_frequency: 1,
  is_enabled: true,
  is_invoiced: false
};

function CompanyEditLicense() {
  const session = useContext(SessionContext);
  const history = useHistory();
  const [licenseId, setLicenseId] = useState();
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [disableSave, setDisableSave] = useState(false);
  const [licensesList, setLicensesList] = useState([]);
  const [showOverlapError, setShowOverlapError] = useState(false);
  const [reloadMessage, setReloadMessage] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDeleteFailed, setShowDeleteFailed] = useState(false);
  const [successLabel, setSuccessLabel] = useState("");
  const [deleteFailedLabel, setDeleteFailedLabel] = useState("");

  const [license, setLicense] = useState(initValues);

  const defaultErrors = {
    fee: "",
    start_date: "",
    monthly_frequency: "",
  };

  const [errors, setErrors] = useState({ ...defaultErrors });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const editLicenseId = urlParams.get("license_id");
    setCompanyId(urlParams.get("company_id"));
    setCompanyName(urlParams.get("name"));

    // if no company id: send user back
    if (!urlParams.get("company_id")) {
      console.log("There is no company ID");
      history.push("/companies");
    }

    if (editLicenseId) {
      // fetch info about license
      setLicenseId(editLicenseId);
      session
        .query("admin/get_licence_fee", { id: editLicenseId })
        .then((response) => {
          setLicense({
            fee: response.fee,
            start_date: dayjs(response.start_date).format("YYYY-MM-DD"),
            end_date: dayjs(response.end_date).format("YYYY-MM-DD"),
            monthly_frequency: response.monthly_frequency,
            is_enabled: response.is_enabled,
            is_invoiced: response.has_been_invoiced
          });
        });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // get all existing licenses
    if(companyId) {
      let urlBuilder = `admin/license_fee_list?am_company_id=${companyId}&sort_by=start_date&sort_asc=true&filters=%7B%7D&sort_dir=asc`;
      session.query(urlBuilder)
      .then((licenses) => {
        setLicensesList(licenses.rows);
      })
    }
    // eslint-disable-next-line
  }, [companyId])

  function checkDatesOverlap(newStart, newEnd, _licenseId) {
    if(!licensesList.length) {
      return false;
    }
    if(!license.is_enabled) {
      return false;
    }

    // only compare against active licenses
    let _list = [];
    licensesList.forEach((lic) => {
      if(lic.is_active && lic.is_enabled) {
        _list.push(lic);
      }
    })

    if(_licenseId) {
      // remove self from list when editing
      _list = _list.filter((item) => item.id !== Number(_licenseId));
    }
    let isOverlapping = false;
    let new_start = new Date(newStart);
    let new_end = new Date(newEnd);

    for (const lic of _list) {
      let old_start = new Date(lic.start_date);
      let old_end = new Date(lic.end_date);
      let overlaps = (new_start >= old_start && new_start <= old_end) ||  // check if new date starts inside an existing date
                     (new_end >= old_start && new_end <= old_end) ||      // check if new date ends inside an existing date
                     (new_start <= old_start && new_end >= old_end);      // check if new date contains an existing date
      
      if(overlaps) { 
        isOverlapping = true;
        setReloadMessage(!reloadMessage);
        break;
      }
    }

    return isOverlapping;
  }

  const onSuccess = () => {
    setTimeout(() => {
      setDisableSave(true);
      setShowSuccess(false);
      setLicense(initValues);
      history.push(`/admin-company-license#company_id=${companyId}`);
    }, 2000);
  };

  function onClickSave(ev) {
    if (ev) {
      ev.stopPropagation();
      ev.preventDefault();
    }

    setShowOverlapError(false);

    let newLicense = {
      id: Number(licenseId),
      fee: Number(license.fee),
      start_date: dayjs(license.start_date).$d,
      end_date: dayjs(license.end_date).add(1, 'day').subtract(1, 'second').$d,
      monthly_frequency: Number(license.monthly_frequency),
      is_enabled: Boolean(license.is_enabled),
      am_company_id: Number(companyId),
    };

    if( checkDatesOverlap(newLicense.start_date, newLicense.end_date, licenseId) ) {
      // if the new license dates overlap with an existing, show message, don't submit
      setShowOverlapError(true);
      return;
    }

    if (!!licenseId) {
      session
        .command(
          "admin/update_license_fee",
          newLicense,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          setSuccessLabel("License Fee Updated.");
          setShowSuccess(true);
          onSuccess();
        })
        .catch((error) => {
          console.log("License Fee Failed to Update", JSON.stringify(error));
        });
    } else {
      session
        .command(
          "admin/create_license_fee",
          newLicense,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          setSuccessLabel("License Fee Created.");
          setShowSuccess(true);
          onSuccess();
        })
        .catch((error) => {
          console.log("License Fee Failed to Create", JSON.stringify(error));
        });
    }
  }

  function handleDelete() {
    session
      .command(
        "admin/delete_license_fee",
        { id: licenseId },
        {},
        { defaultErrorHandling: false }
      )
      .then((res) => {
        if(res.deleteSuccess) {
          setSuccessLabel("License Fee Deleted.");
          setShowSuccess(true);
          onSuccess();
        } else {
          setDeleteFailedLabel(res.message || "You cannot delete invoiced license fees.");
          setShowDeleteFailed(true);
        }
        
      })
      .catch((error) => {
        console.log("License Fee Failed to Delete", JSON.stringify(error));
      });
  }

  function updateLicense(field, ev) {
    setLicense({ ...license, [field]: ev.target.value });
  }

  function handleEnabledChange(ev) {
    setLicense({ ...license, is_enabled: ev });
  }

  useEffect(() => {
    setDisableSave(false);
    if (
      license.fee <= 0 ||
      license.start_date.length < 1 ||
      license.monthly_frequency <= 0
      ) {
      setDisableSave(true);
    }
  }, [license]);

  useEffect(() => {
    if (license.fee < 0) {
      setLicense({ ...license, fee: 0 });
      setErrors({ ...errors, fee: "*The fee must be greater than 0" });
    } else {
      setErrors({ ...errors, fee: "" });
    }
    // eslint-disable-next-line
  }, [license.fee]);

  useEffect(() => {
    function checkStartDateIsValid() {
      // set the minimum start date to the 1 of current month
      let selected = dayjs(license.start_date);
      let min_date_allowed = dayjs().date(1);
      let min_set = dayjs().date(1).format("YYYY-MM-DD");
      if(selected < min_date_allowed) {
        if(dayjs(selected).date() !== 1) {
          setTimeout(() => {
            setLicense({ ...license, start_date: min_set });
          }, 100)
          setErrors({
            ...errors,
            start_date:
              "*Start dates are on or after the 1st date of the current month",
          });
        }
      }
    }

    if (license.start_date.length < 1) {
      setDisableSave(true);
    } else {
      setErrors({ ...errors, start_date: "" });
      checkStartDateIsValid();
    }
    // eslint-disable-next-line
  }, [license.start_date]);

  useEffect(() => {
    if (license.monthly_frequency < 0) {
      setLicense({ ...license, monthly_frequency: 0 });
      setErrors({
        ...errors,
        monthly_frequency: "*Default and minimum billing period is one month",
      });
    } else {
      setErrors({ ...errors, monthly_frequency: "" });
    }
    
    if(license.monthly_frequency.toString().includes(".")) {
      setTimeout( () => {
        setLicense({ ...license, monthly_frequency: Math.floor(license.monthly_frequency)})
      }, 100);
    }
    // eslint-disable-next-line
  }, [license.monthly_frequency]);

  // set the end date based on the start date
  useEffect(() => {
    if (license.monthly_frequency > 0 && license.start_date.length > 0 && !licenseId && license.end_date.length < 1) {
      const s_date = dayjs(license.start_date);
      let selectedMonth = s_date.month();
      s_date.month(selectedMonth + Number(license.monthly_frequency));
      let setEndDate = s_date.month(selectedMonth + Number(license.monthly_frequency)).subtract(1, 'second').format("YYYY-MM-DD");
      setLicense({ ...license, end_date: setEndDate });
    }
    if(dayjs(license.end_date) < dayjs(license.start_date).add(license.monthly_frequency, 'month')) {
      setLicense({ ...license, end_date: dayjs(license.start_date).add(license.monthly_frequency, 'month').subtract(1, 'second').format("YYYY-MM-DD") });
    }
    // eslint-disable-next-line
  }, [license.start_date, license.monthly_frequency, license.end_date]);

  return (
    <div className="page orders-page admin-page">
      <div className="list-header sticky-page-header">
        <Link to={{pathname: `/admin-company-license`, hash: `#company_id=${companyId}`}}>
          <button type="button" className="new-button">
            <i className="bi bi-chevron-left"></i> Company Licenses
          </button>
        </Link>
        <h1>License Details</h1>
        <i className="spacer" />
        <button
          className="save-button"
          onClick={onClickSave}
          disabled={disableSave}
        >
          Save Changes
        </button>
      </div>
      <form className="company-form-page" onSubmit={e => e.preventDefault()}>
        <section className="license-details">
          <br />
          <h4>
            Company:{" "}
            {companyName && <span style={nameStyle}>{companyName}</span>}
          </h4>
          {showOverlapError && (<>
            <br />
            <DatesOverlapMessage reload={reloadMessage} />
          </>)}
          <br />
          <LicenseFields
            license={license}
            updateLicense={updateLicense}
            handleEnabledChange={handleEnabledChange}
            errors={errors}
            handleDelete={handleDelete}
            licenseId={licenseId}
            setDisableSave={setDisableSave}
          />
        </section>
      </form>
      <ConfirmSuccess show={showSuccess} label={successLabel} />
      <UnsavedChanges hasUnsavedChanges={!disableSave} />
      <FailedToDelete show={showDeleteFailed} label={deleteFailedLabel} onClose={() => setShowDeleteFailed(false)} />
    </div>
  );
}

export default CompanyEditLicense;

/**
 * fields to edit or create a license
 */
function LicenseFields({
  license,
  updateLicense,
  errors,
  handleEnabledChange,
  handleDelete,
  licenseId = null,
  setDisableSave,
}) {
  const [dataLoaded, setDataLoaded] = useState();
  const [originalData, setOriginalData] = useState();
  const [showModalDelete, setShowModalDelete] = useState(false);

  useEffect(() => {
    if (!dataLoaded) {
      setDataLoaded(true);
      setOriginalData(license);
    }
    // eslint-disable-next-line
  }, [license]);

  useEffect(() => {
    function checkForChanges() {
      if (
        license.fee === originalData.fee &&
        license.start_date === originalData.start_date &&
        license.monthly_frequency === originalData.monthly_frequency &&
        license.is_enabled === originalData.is_enabled
        ) {
        setDisableSave(true);
      }
    };

    if (licenseId !== null && originalData) {
      setTimeout(() => {
        checkForChanges();
      }, 500)
    }
    // eslint-disable-next-line
  }, [license, originalData]);

  function confirmDelete() {
    setShowModalDelete(true);
  }

  function onClickDelete() {
    setShowModalDelete(false);
    handleDelete();
  }

  return (
    <>
      <Field label="* Amount" className="license-heading">
        <input
          type="number"
          value={license.fee}
          className="license-info"
          onChange={(ev) => updateLicense("fee", ev)}
          min="0"
          disabled={license.is_invoiced}
        />
        {errors.fee && <span className="error-msg">{errors.fee}</span>}
      </Field>

      <Field label="* Start Date" className="license-heading">
        <input
          type="date"
          value={license.start_date}
          className="license-info"
          onChange={(ev) => updateLicense("start_date", ev)}
          disabled={license.is_invoiced}
        />
        {errors.start_date && (
          <span className="error-msg">{errors.start_date}</span>
        )}
      </Field>

      <Field label="* End Date" className="license-heading">
        <input
          type="date"
          value={license.end_date}
          className="license-info"
          onChange={(ev) => updateLicense("end_date", ev)}
        />
      </Field>

      <Field label="* Frequency" className="license-heading">
        <input
          type="number"
          min="0"
          value={license.monthly_frequency}
          className="license-info frequency"
          onChange={(ev) => updateLicense("monthly_frequency", ev)}
        />{" "}
        <em className="months">(months)</em>{" "}
        {errors.monthly_frequency && (
          <span className="error-msg">{errors.monthly_frequency}</span>
        )}
      </Field>

      <div className="license-enable">
        <span className="license-label-text">* License Status:</span>
        <span className="radio-wrapper">
          <label>
            <input
              type="checkbox"
              className="enable-check"
              checked={license.is_enabled}
              onChange={() => handleEnabledChange(true)}
            />
            Active
          </label>
          <span className="spacer" />
          <label>
            <input
              type="checkbox"
              className="enable-check"
              checked={!license.is_enabled}
              onChange={() => handleEnabledChange(false)}
            />
            Inactive
          </label>
        </span>
      </div>
      <br />
      <div className="license-enable">
        <Button
          onClick={confirmDelete}
          disabled={!licenseId}
        >
          Delete This License
        </Button>
      </div>
      <ConfirmDelete
        show={showModalDelete}
        label="Delete this license permanently?"
        proceed={onClickDelete}
        cancel={() => {setShowModalDelete(false)}}
      />
    </>
  );
}

/*
* component to show a message about the dates overlapping
*/

const DatesOverlapMessage = ({reload}) => {

  const [showLoader, setShowLoader] = useState(true);

  const loaderWrap = {
    'paddingLeft': '1em'
  }

  useEffect(() => {
    setShowLoader(true);
    setTimeout(() => {
      setShowLoader(false)
    }, 1500);
  }, [reload])

  if(showLoader) {
    return <div style={loaderWrap}><ProgressLoaderSimple /></div>
  }

  return (
    <div className="generic-error-msg">
      *These dates overlap one or more existing licenses.<br />
      Please check the previous screen for conflicting licenses.
    </div>
  )
}